import BaseApi from '@/api/Base/BaseApi'
import {
  CreateValidationNodePayload,
  GetValidationStatusResponse,
  GetValidationResultResponse,
  UpdateValidationNodePayload,
  ValidationNodeInfo,
  UpdateThresholdPayload,
  StartValidationPayload,
  GetValidationResultItemsResponse,
  ValidationResultItemsPayload,
  GetValidationInformationResponse,
  ExportValidationDatasetPayload,
  CommonValidationResult,
  GetTabularResultItemsResponse
} from '@/api/ValidationNode/types'
import { GetLabelingNodeItemsResponse } from '@/api/LabelingNode/types'
import { CommonListPayload } from '@/api/DatasetNode/types'

const ROOT = '/validation'
const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class ValidationNodeApi extends BaseApi {
  createValidationNode(payload: CreateValidationNodePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post<ValidationNodeInfo>(`/project/${projectId}/validation/create`, rest)
  }

  getValidationNodeDetail(validationNodeId: string) {
    return this.agent.get<ValidationNodeInfo>(`${ROOT}/${validationNodeId}`)
  }

  updateValidationNode(payload: UpdateValidationNodePayload) {
    const { validationNodeId, name, ...rest } = payload
    return this.agent.put<ValidationNodeInfo>(`${ROOT}/${validationNodeId}`, { ...rest, name: name?.trim() })
  }

  deleteValidationNode(validationNodeId: string) {
    return this.agent.delete(`${ROOT}/${validationNodeId}`)
  }

  startValidation(payload: StartValidationPayload) {
    const { validationNodeId, ...rest } = payload
    return this.agent.post<GetValidationStatusResponse>(`${ROOT}/${validationNodeId}/validate`, rest)
  }

  stopValidation(validationNodeId: string) {
    return this.agent.put<GetValidationStatusResponse>(`${ROOT}/${validationNodeId}/validate/stop`)
  }

  getValidationStatus(validationNodeId: string) {
    return this.agent.get<GetValidationStatusResponse>(`${ROOT}/${validationNodeId}/validate/status`)
  }

  getValidationResult(validationNodeId: string) {
    return this.agent.get<GetValidationResultResponse>(`${ROOT}/${validationNodeId}/validate`)
  }

  getValidationInformation(validationNodeId: string) {
    return this.agent.get<GetValidationInformationResponse>(`${ROOT}/${validationNodeId}/information`)
  }

  getValidationItems(validationNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? defaultPaginationOptions
    return this.agent.get<GetLabelingNodeItemsResponse>(`${ROOT}/${validationNodeId}/items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getValidationResultSummary(validationNodeId: string) {
    return this.agent.get<CommonValidationResult>(`${ROOT}/${validationNodeId}/summary`)
  }

  getValidationResultItems(validationNodeId: string, options?: ValidationResultItemsPayload) {
    const {
      isNG,
      includeDetectedObjects,
      filterClassIds,
      page,
      itemsPerPage,
      sortDir,
      sortKey,
    } = options ?? { ...defaultPaginationOptions }

    return this.agent.get<GetValidationResultItemsResponse>(`${ROOT}/${validationNodeId}/result-items`, {
      params: {
        isNG,
        includeDetectedObjects,
        filterClassIds,
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  updateThreshold(payload: UpdateThresholdPayload) {
    const { validationNodeId, ...rest } = payload
    return this.agent.put<GetValidationStatusResponse>(`${ROOT}/${validationNodeId}/threshold`, rest)
  }

  exportValidationDataset(payload: ExportValidationDatasetPayload) {
    const { validationNodeId, nodeName, ...rest } = payload
    return this.agent.post(`${ROOT}/${validationNodeId}/exports`, rest, {
      responseType: 'blob',
    })
  }

  getTabularResult(validationNodeId: string) {
    return this.agent.get<any>(`${ROOT}/${validationNodeId}/tabular-result`)
  }

  /** @deprecated */
  getTabularResultItems(validationNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? defaultPaginationOptions
    return this.agent.get<GetTabularResultItemsResponse>(`${ROOT}/${validationNodeId}/tabular-result-items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  /** @deprecated */
  getWindowItems(validationNodeId: string, startValue: any, endValue: any) {
    return this.agent.get<any>(`${ROOT}/${validationNodeId}/window-items`, {
      params: {
        startValue,
        endValue,
      },
    })
  }
}
